import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setNotification } from "../../redux/slices/notificationSlice";
import { messaging, onMessage } from "../../config/firebaseConfig";
import PaymentInstructionVA from "../components/PaymentInstructionVA";
import PaymentInstructionOffline from "../components/PaymentInstructionOffline";
import PaymentInstructionQRIS from "../components/PaymentInstructionQRIS";
import PaymentInstructionEwallet from "../components/PaymentInstructionEwallet";
import PaymentInstructionCard from "../components/PaymentInstructionCard";
import { registerServiceWorker } from "../../config/firebaseConfig";
import { getPaymentLinkHeaders } from "../../utils/paymentLinkHeaders"; // Import secure headers utility
import {
  handlePaymentLinkError,
  createErrorNavigationState,
  PaymentLinkErrorCode
} from "../../utils/paymentLinkErrorHandler";
import { getEarliestExpiration } from "../../utils/paymentLinkExpiration";
import { resolveEwalletInstruction } from "../utils/ewalletFlowRouter";

const BASE_URL = process.env.REACT_APP_API_URL;

const ThirdForm = () => {
  // untuk terima notif di background firebase
  registerServiceWorker();

  const location = useLocation();
  const navigate = useNavigate();
  const { reffNo } = useParams();
  const dispatch = useDispatch();
  // const [loading, setLoading] = useState(false);

  // ✅ Deklarasikan state yang dibutuhkan sebelum hooks lain
  const [historyId, setHistoryId] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ Fungsi untuk fetch data dari API berdasarkan reffNo
  const fetchPaymentData = useCallback(async (reffNoParam) => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/payment-link/get-by-reff`, {
        method: "POST",
        headers: getPaymentLinkHeaders(),
        body: JSON.stringify({ reff_no: reffNoParam }),
      });

      if (!response.ok) {
        // Handle error with proper error codes
        const errorInfo = await handlePaymentLinkError(response);
        console.error("Payment link error:", errorInfo);

        // Extract payment data from error response (if available)
        const paymentData = errorInfo.paymentData || {};

        // Navigate to appropriate error page based on error code
        if (errorInfo.isExpired) {
          navigate("/payment-expired", {
            replace: true,
            state: {
              ...createErrorNavigationState(errorInfo, {
                reffNo: paymentData.reff_no || reffNoParam,
              }),
              selectedMethod: paymentData.payment_method_name || "-",
              amount: paymentData.amount || 0,
              redirectUrl: paymentData.redirect_url || null,
            },
          });
        } else if (errorInfo.isPaid) {
          navigate("/payment-success", {
            replace: true,
            state: {
              reffNo: paymentData.reff_no || reffNoParam,
              amount: paymentData.amount || 0,
              customerName: paymentData.customer_name || null,
              email: paymentData.customer_email || null,
              paymentMethod: paymentData.payment_method_name || "-",
              paymentDate: paymentData.payment_date || "-",
              redirectUrl: paymentData.redirect_url || null,
            },
          });
        } else {
          // For other errors (not found, failed, etc.), navigate to generic error
          navigate("/wrong-page");
        }
        return;
      }

      const data = await response.json();
      // ✅ Struktur response dari get-by-reff sudah sama dengan proceed-inquiry
      // Response: { success, code, data: { paymentLink, paymentLinkHistory, paymentMethod } }
      setPaymentData({
        amount: data.data.paymentLinkHistory.amount,
        orderId: data.data.paymentLinkHistory.reff_no,
        result: data, // ✅ Simpan seluruh response object, sama seperti di SecondForm
      });

      setHistoryId(data.data.paymentLinkHistory.id);
    } catch (error) {
      console.error("Error fetching payment data:", error);
      navigate("/wrong-page");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ✅ Pastikan `useEffect` tetap dipanggil setiap render
  useEffect(() => {
    // Jika ada state dari navigate, gunakan itu dan update URL jika belum ada reffNo di URL
    if (location.state?.result) {
      const reffNoFromState = location.state.result.data.paymentLinkHistory.reff_no;

      // Update URL jika belum ada reffNo di URL/ tidak perlu sudah ada di second form
      // if (!reffNo) {
      //   navigate(`/instruction/${reffNoFromState}`, {
      //     replace: true,
      //     state: location.state,
      //   });
      // }

      // Set data dari state
      setPaymentData({
        amount: location.state.amount,
        orderId: location.state.orderId,
        result: location.state.result,
      });
      setHistoryId(location.state.result.data.paymentLinkHistory.id);
    }
    // Jika tidak ada state tapi ada reffNo di URL, fetch dari API
    else if (reffNo) {
      fetchPaymentData(reffNo);
    }
    // Jika tidak ada keduanya, redirect ke error page
    else {
      navigate("/wrong-page");
      console.log("Redirecting to /wrong-page...");
    }
  }, [location.state, reffNo, navigate, fetchPaymentData]);

  // ✅ Hooks `useCallback` tetap dipanggil di tempat yang benar
  const checkTransactionStatus = useCallback(async () => {
    if (!historyId) return null;

    try {
      const response = await fetch(`${BASE_URL}/api/payment-link/check-status`, {
        method: "POST",
        headers: getPaymentLinkHeaders(),
        body: JSON.stringify({
          history_id: historyId,
          reff_no: paymentData?.result?.data?.paymentLinkHistory?.reff_no || paymentData?.orderId // Required by middleware
        }),
      });

      const data = await response.json();
      const history_check = data.transaction;

      console.log(history_check);

      if (history_check.status === "paid") {
        dispatch(
          setNotification({
            type: "success",
            title: "Status Transaksi",
            message: "Pembayaran telah diterima",
          })
        );

        setTimeout(() => {
          const paramData = {
            reffNo: history_check.reff_no || "-",
            amount: history_check.amount || "-",
            customerName: history_check.customer_name || null,
            email: history_check.customer_email || null,
            paymentMethod: history_check.payment_method || "-",
            paymentDate: history_check.payment_date || "-",
            redirectUrl: history_check.redirect_url || paymentData?.result?.data?.paymentLink?.redirect_url || null,
          };
          navigate("/payment-success", {
            replace: true,
            state: paramData,
          });
        }, 3000);
      } else if (history_check.status === "expired") {
        dispatch(
          setNotification({
            type: "warning",
            title: "Status Transaksi",
            message: `Status transaksi ini adalah ` + history_check.status,
          })
        );

        setTimeout(() => {
          navigate("/payment-expired", {
            replace: true,
            state: {
              selectedMethod: history_check.payment_method || "-",
              reffNo: history_check.reff_no || "-",
              amount: history_check.amount || "-",
              redirectUrl: history_check.redirect_url || paymentData?.result?.data?.paymentLink?.redirect_url || null,
            },
          });
        }, 3000);
      } else if (history_check.status === "failed") {
        dispatch(
          setNotification({
            type: "error",
            title: "Status Transaksi",
            message: "Pembayaran gagal. Silakan coba lagi.",
          })
        );
      } else {
        dispatch(
          setNotification({
            type: "info",
            title: "Status Transaksi",
            message: `Status transaksi ini adalah ` + history_check.status,
          })
        );
      }

      return history_check?.status ?? null;
    } catch (error) {
      console.error("Error checking transaction status:", error);
      dispatch(
        setNotification({
          type: "error",
          title: "Error",
          message: "Gagal mengecek status transaksi.",
        })
      );
      return null;
    }
  }, [historyId, paymentData, dispatch, navigate]);

  // ✅ useEffect tetap dipanggil setiap render
  useEffect(() => {
    if (!historyId) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("Message received: ", payload);

      const status = payload?.data?.status;
      if (status === "paid" || status === "expired") {
        console.log("just start to check in server..");
        checkTransactionStatus();
      } else {
        dispatch(
          setNotification({
            type: "info",
            title: "Notifikasi Pembayaran",
            message: payload?.notification?.body,
          })
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [historyId, dispatch, checkTransactionStatus]);

  // ✅ Pastikan return hanya terjadi di bawah semua hooks
  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  if (!paymentData) {
    return null;
  }

  const { amount, orderId, result } = paymentData;
  console.log(result);

  const renderPaymentInstruction = () => {
    // Calculate earliest expiration between PaymentLink and PaymentLinkHistory
    const paymentLinkExpiredAt = result.data.paymentLink.expired_at;
    const paymentLinkHistoryExpiredAt = result.data.paymentLinkHistory.expired_at;
    const earliestExpiration = getEarliestExpiration(paymentLinkExpiredAt, paymentLinkHistoryExpiredAt);

    if (process.env.REACT_APP_DEBUG) {
      console.log('Expiration Info:', {
        paymentLinkExpiredAt,
        paymentLinkHistoryExpiredAt,
        earliestExpiration,
        source: earliestExpiration === paymentLinkExpiredAt ? 'PaymentLink' :
                earliestExpiration === paymentLinkHistoryExpiredAt ? 'PaymentLinkHistory' : 'Default'
      });
    }

    const instructionProps = {
      amount,
      orderId,
      merchantName: result.data.paymentLink.account.merchant.name,
      expired_at: earliestExpiration, // Use earliest expiration instead of just PLH expired_at
      selected_method: result.data.paymentLinkHistory.payment_method_name,
      onCheckStatus: checkTransactionStatus, // Kirim fungsi ke layout
    };

    switch (result.data.paymentMethod.group) {
      case "offline_store":
        return (
          <PaymentInstructionOffline
            {...instructionProps}
            paymentCode={result.data.paymentLinkHistory.payment_method_value}
            storeName={result.data.paymentLinkHistory.payment_method_name}
            storeCode={result.data.paymentMethod.code}
          />
        );
      case "va":
        return (
          <PaymentInstructionVA
            {...instructionProps}
            virtualAccount={result.data.paymentLinkHistory.payment_method_value}
            bankName={result.data.paymentLinkHistory.payment_method_name}
            bankCode={result.data.paymentMethod.code}
          />
        );
      case "qris":
        return (
          <PaymentInstructionQRIS
            {...instructionProps}
            qrString={result.data.paymentLinkHistory.payment_method_additional}
          />
        );
      case "ewallet": {
        let ewalletData = null;
        try {
          ewalletData = JSON.parse(result.data.paymentLinkHistory.payment_method_additional || '{}');
        } catch (e) {
          console.error('Failed to parse payment_method_additional:', e);
        }

        const waitingRedirect = resolveEwalletInstruction(result, ewalletData, orderId, amount);
        if (waitingRedirect?.shouldRedirectToWaiting) {
          navigate(waitingRedirect.route, { replace: true, state: waitingRedirect.state });
          return null;
        }

        const ewalletCode = result.data.paymentMethod.code;
        const ewalletUrl = ewalletData?.web_redirect_url || null;

        return (
          <PaymentInstructionEwallet
            {...instructionProps}
            ewalletName={result.data.paymentLinkHistory.payment_method_name}
            ewalletCode={ewalletCode}
            ewalletUrl={ewalletUrl}
          />
        );
      }
      case "card":
        return (
          <PaymentInstructionCard
            {...instructionProps}
            bankName={result.data.paymentLinkHistory.payment_method_name}
            bankCode={result.data.paymentMethod.code}
          />
        );
      default:
        return <p>Metode pembayaran tidak dikenali</p>;
    }
  };

  return <div>{renderPaymentInstruction()}</div>;
};

export default ThirdForm;
